(function () {
  'use strict';

  function initCleenyHeader(section) {
    if (!section || section.dataset.cleenyHeaderReady === 'true') return;
    section.dataset.cleenyHeaderReady = 'true';

    var wrapper = section.closest('.shopify-section');
    if (wrapper) wrapper.classList.add('cleeny-header-shopify-wrapper');

    var spacer = null;
    var resizeObserver = null;
    var resizeHandler = null;

    function setupFixedHeader() {
      if (!wrapper) return;

      var sectionId = section.dataset.sectionId || section.id || 'cleeny-header';
      var spacerSelector = '[data-cleeny-header-spacer="' + sectionId.replace(/"/g, '') + '"]';
      spacer = wrapper.nextElementSibling;

      if (!spacer || !spacer.matches(spacerSelector)) {
        spacer = document.createElement('div');
        spacer.className = 'cleeny-header-fixed-spacer';
        spacer.setAttribute('data-cleeny-header-spacer', sectionId);
        spacer.setAttribute('aria-hidden', 'true');
        wrapper.insertAdjacentElement('afterend', spacer);
      }

      function syncHeight() {
        if (!section.isConnected || !spacer || !spacer.isConnected) return;
        var height = Math.ceil(section.getBoundingClientRect().height);
        if (!height) return;
        spacer.style.setProperty('--cleeny-fixed-header-height', height + 'px');
        spacer.style.height = height + 'px';
      }

      resizeHandler = syncHeight;
      syncHeight();
      window.requestAnimationFrame(syncHeight);
      window.setTimeout(syncHeight, 120);
      window.setTimeout(syncHeight, 500);
      window.addEventListener('resize', syncHeight, { passive: true });

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(syncHeight);
        resizeObserver.observe(section);
      }
    }

    function cleanupFixedHeader() {
      if (resizeObserver) resizeObserver.disconnect();
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
      if (spacer && spacer.parentNode) spacer.parentNode.removeChild(spacer);
      resizeObserver = null;
      resizeHandler = null;
      spacer = null;
    }

    setupFixedHeader();

    var menuToggle = section.querySelector('[data-cleeny-menu-toggle]');
    var mobileMenu = section.querySelector('[data-cleeny-mobile-menu]');
    var cartDrawer = section.querySelector('[data-cleeny-cart-drawer]');
    var cartOverlay = section.querySelector('[data-cleeny-cart-overlay]');
    var cartContent = section.querySelector('[data-cleeny-cart-content]');
    var cartCount = section.querySelector('[data-cleeny-cart-count]');
    var cartSubtotal = section.querySelector('[data-cleeny-cart-subtotal]');
    var checkoutLink = section.querySelector('[data-cleeny-checkout]');
    var cartOpenButtons = section.querySelectorAll('[data-cleeny-cart-open]');
    var cartCloseButtons = section.querySelectorAll('[data-cleeny-cart-close]');

    var currency = section.dataset.currency || 'USD';
    var lastFocusedElement = null;
    var cartRequestInFlight = false;

    function rootUrl() {
      return window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    }

    function formatMoney(cents) {
      var amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2
        }).format(amount);
      } catch (error) {
        return '$' + amount.toFixed(2);
      }
    }

    function setDocumentClass(className, enabled) {
      document.documentElement.classList.toggle(className, enabled);
      document.body.classList.toggle(className, enabled);
    }

    function closeMenu() {
      if (!mobileMenu || !menuToggle) return;
      mobileMenu.classList.remove('is-open');
      menuToggle.setAttribute('aria-expanded', 'false');
      menuToggle.setAttribute('aria-label', 'Open menu');
      setDocumentClass('cleeny-menu-open', false);

      window.setTimeout(function () {
        if (!mobileMenu.classList.contains('is-open')) mobileMenu.hidden = true;
      }, 430);
    }

    function openMenu() {
      if (!mobileMenu || !menuToggle) return;
      closeCart(false);
      mobileMenu.hidden = false;

      window.requestAnimationFrame(function () {
        mobileMenu.classList.add('is-open');
        menuToggle.setAttribute('aria-expanded', 'true');
        menuToggle.setAttribute('aria-label', 'Close menu');
        setDocumentClass('cleeny-menu-open', true);
      });
    }

    function toggleMenu() {
      if (!mobileMenu || !menuToggle) return;
      if (mobileMenu.classList.contains('is-open')) closeMenu();
      else openMenu();
    }

    function setCartLoading(loading) {
      if (cartDrawer) cartDrawer.classList.toggle('is-loading', Boolean(loading));
    }

    async function fetchCart() {
      var response = await fetch(rootUrl() + 'cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        cache: 'no-store'
      });

      if (!response.ok) throw new Error('Unable to load cart.');
      return response.json();
    }

    function updateCartCount(cart) {
      if (!cartCount) return;
      var count = Number(cart && cart.item_count ? cart.item_count : 0);
      cartCount.textContent = String(count);
      cartCount.classList.toggle('is-empty', count === 0);
    }

    function createEmptyCart() {
      var empty = document.createElement('div');
      empty.className = 'cleeny-cart-empty';

      var mark = document.createElement('div');
      mark.className = 'cleeny-cart-empty__mark';
      mark.textContent = 'C';

      var title = document.createElement('h3');
      title.textContent = 'YOUR BASKET IS EMPTY';

      var text = document.createElement('p');
      text.textContent = 'Add your cleaning essentials and keep laundry day simple.';

      empty.appendChild(mark);
      empty.appendChild(title);
      empty.appendChild(text);
      return empty;
    }

    function createCartItem(item) {
      var article = document.createElement('article');
      article.className = 'cleeny-cart-item';

      var imageLink = document.createElement('a');
      imageLink.className = 'cleeny-cart-item__image-link';
      imageLink.href = item.url || '#';

      if (item.image) {
        var image = document.createElement('img');
        image.className = 'cleeny-cart-item__image';
        image.src = item.image;
        image.alt = item.product_title || item.title || 'Cart item';
        image.loading = 'lazy';
        image.width = 192;
        image.height = 224;
        imageLink.appendChild(image);
      }

      var main = document.createElement('div');
      main.className = 'cleeny-cart-item__main';

      var top = document.createElement('div');
      top.className = 'cleeny-cart-item__top';

      var titleLink = document.createElement('a');
      titleLink.className = 'cleeny-cart-item__title';
      titleLink.href = item.url || '#';
      titleLink.textContent = item.product_title || item.title || 'Product';

      var priceWrap = document.createElement('div');

      var price = document.createElement('span');
      price.className = 'cleeny-cart-item__price';
      price.textContent = formatMoney(item.final_line_price);
      priceWrap.appendChild(price);

      if (Number(item.original_line_price || 0) > Number(item.final_line_price || 0)) {
        var compare = document.createElement('div');
        compare.className = 'cleeny-cart-item__compare';
        compare.textContent = formatMoney(item.original_line_price);
        priceWrap.appendChild(compare);
      }

      top.appendChild(titleLink);
      top.appendChild(priceWrap);
      main.appendChild(top);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        var variant = document.createElement('p');
        variant.className = 'cleeny-cart-item__variant';
        variant.textContent = item.variant_title;
        main.appendChild(variant);
      }

      var bottom = document.createElement('div');
      bottom.className = 'cleeny-cart-item__bottom';

      var quantity = document.createElement('div');
      quantity.className = 'cleeny-cart-item__quantity';
      quantity.setAttribute('aria-label', 'Quantity');

      var minus = document.createElement('button');
      minus.type = 'button';
      minus.textContent = '−';
      minus.setAttribute('aria-label', 'Decrease quantity');
      minus.addEventListener('click', function () {
        changeCartItem(item.key, Math.max(0, Number(item.quantity) - 1));
      });

      var number = document.createElement('span');
      number.textContent = String(item.quantity);
      number.setAttribute('aria-live', 'polite');

      var plus = document.createElement('button');
      plus.type = 'button';
      plus.textContent = '+';
      plus.setAttribute('aria-label', 'Increase quantity');
      plus.addEventListener('click', function () {
        changeCartItem(item.key, Number(item.quantity) + 1);
      });

      quantity.appendChild(minus);
      quantity.appendChild(number);
      quantity.appendChild(plus);

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cleeny-cart-item__remove';
      remove.textContent = 'Remove';
      remove.addEventListener('click', function () {
        changeCartItem(item.key, 0);
      });

      bottom.appendChild(quantity);
      bottom.appendChild(remove);
      main.appendChild(bottom);

      article.appendChild(imageLink);
      article.appendChild(main);
      return article;
    }

    function renderCart(cart) {
      if (!cartContent) return;
      cartContent.innerHTML = '';

      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        cartContent.appendChild(createEmptyCart());
      } else {
        cart.items.forEach(function (item) {
          cartContent.appendChild(createCartItem(item));
        });
      }

      updateCartCount(cart);
      if (cartSubtotal) cartSubtotal.textContent = formatMoney(cart.total_price || 0);

      if (checkoutLink) {
        var empty = !cart || !cart.item_count;
        checkoutLink.classList.toggle('is-disabled', empty);
        checkoutLink.setAttribute('aria-disabled', empty ? 'true' : 'false');
      }
    }

    async function refreshCart() {
      if (cartRequestInFlight) return null;
      cartRequestInFlight = true;
      setCartLoading(true);

      try {
        var cart = await fetchCart();
        renderCart(cart);
        return cart;
      } catch (error) {
        console.error('CLEENY cart refresh error:', error);
        return null;
      } finally {
        cartRequestInFlight = false;
        setCartLoading(false);
      }
    }

    async function changeCartItem(itemKey, quantity) {
      if (cartRequestInFlight || !itemKey) return;
      cartRequestInFlight = true;
      setCartLoading(true);

      try {
        var response = await fetch(rootUrl() + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: itemKey, quantity: quantity })
        });

        var cart = await response.json().catch(function () { return {}; });
        if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to update cart.');

        renderCart(cart);
        publishCartUpdate(cart);
      } catch (error) {
        console.error('CLEENY cart update error:', error);
        await refreshCart();
      } finally {
        cartRequestInFlight = false;
        setCartLoading(false);
      }
    }

    function publishCartUpdate(cart) {
      try {
        document.dispatchEvent(new CustomEvent('cleeny:cart:updated', { detail: { cart: cart } }));
      } catch (error) {
        // Compatibility event only.
      }

      try {
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cleeny-header',
            cartData: cart
          });
        }
      } catch (error) {
        // Dawn pub/sub is optional.
      }
    }

    async function addToCart(variantId, quantity, properties) {
      var id = Number(variantId);
      var qty = Math.max(1, Number(quantity || 1));
      if (!id) throw new Error('A valid Shopify variant is required.');

      var item = { id: id, quantity: qty };
      if (properties && typeof properties === 'object') item.properties = properties;

      var response = await fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [item] })
      });

      var data = await response.json().catch(function () { return {}; });
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to add product.');

      var cart = await refreshCart();
      publishCartUpdate(cart || data);
      await openCart(false);
      return data;
    }

    async function openCart(refreshFirst) {
      if (!cartDrawer || !cartOverlay) return;
      closeMenu();
      lastFocusedElement = document.activeElement;

      cartOverlay.hidden = false;
      cartDrawer.setAttribute('aria-hidden', 'false');

      window.requestAnimationFrame(function () {
        cartOverlay.classList.add('is-open');
        cartDrawer.classList.add('is-open');
        setDocumentClass('cleeny-cart-open', true);
      });

      if (refreshFirst !== false) await refreshCart();

      var closeButton = cartDrawer.querySelector('[data-cleeny-cart-close]');
      if (closeButton) closeButton.focus({ preventScroll: true });

      try {
        document.dispatchEvent(new CustomEvent('cleeny:cart:open'));
      } catch (error) {
        // Compatibility event only.
      }
    }

    function closeCart(returnFocus) {
      if (!cartDrawer || !cartOverlay) return;
      cartOverlay.classList.remove('is-open');
      cartDrawer.classList.remove('is-open');
      cartDrawer.setAttribute('aria-hidden', 'true');
      setDocumentClass('cleeny-cart-open', false);

      window.setTimeout(function () {
        if (!cartOverlay.classList.contains('is-open')) cartOverlay.hidden = true;
      }, 240);

      if (returnFocus !== false && lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus({ preventScroll: true });
      }

      try {
        document.dispatchEvent(new CustomEvent('cleeny:cart:close'));
      } catch (error) {
        // Compatibility event only.
      }
    }

    function handleKeydown(event) {
      if (event.key !== 'Escape') return;

      if (cartDrawer && cartDrawer.classList.contains('is-open')) {
        event.preventDefault();
        closeCart(true);
        return;
      }

      if (mobileMenu && mobileMenu.classList.contains('is-open')) {
        event.preventDefault();
        closeMenu();
      }
    }

    if (menuToggle) menuToggle.addEventListener('click', toggleMenu);

    if (mobileMenu) {
      mobileMenu.querySelectorAll('a').forEach(function (link) {
        link.addEventListener('click', closeMenu);
      });
    }

    cartOpenButtons.forEach(function (button) {
      button.addEventListener('click', function () { openCart(true); });
    });

    cartCloseButtons.forEach(function (button) {
      button.addEventListener('click', function () { closeCart(true); });
    });

    if (cartOverlay) cartOverlay.addEventListener('click', function () { closeCart(true); });
    document.addEventListener('keydown', handleKeydown);

    document.addEventListener('cleeny:cart:updated', function (event) {
      if (event.detail && event.detail.cart && Array.isArray(event.detail.cart.items)) {
        renderCart(event.detail.cart);
      } else {
        refreshCart();
      }
    });

    window.addEventListener('pageshow', function () {
      refreshCart();
      if (resizeHandler) resizeHandler();
    });

    section.addEventListener('shopify:section:unload', cleanupFixedHeader, { once: true });

    window.CleenyCart = window.CleenyCart || {};
    window.CleenyCart.open = function () { return openCart(true); };
    window.CleenyCart.close = function () { return closeCart(true); };
    window.CleenyCart.refresh = refreshCart;
    window.CleenyCart.add = addToCart;

    window.openCleenyCartDrawer = function () { return openCart(true); };
    window.closeCleenyCartDrawer = function () { return closeCart(true); };
    window.refreshCleenyCart = refreshCart;

    refreshCart();
  }

  function initAllCleenyHeaders(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-cleeny-header]').forEach(initCleenyHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAllCleenyHeaders(document); });
  } else {
    initAllCleenyHeaders(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAllCleenyHeaders(event.target);
  });
})();